const BACKSLASH = String.fromCharCode(0x5c)

/** `<`, `>`, `&`, and the two line-terminator characters JSON.stringify never escapes. */
const UNSAFE_CHAR_CODES = [0x3c, 0x3e, 0x26, 0x2028, 0x2029]

const UNSAFE_PATTERN = new RegExp(
  `[${UNSAFE_CHAR_CODES.map((code) => String.fromCharCode(code)).join('')}]`,
  'g'
)

function toUnicodeEscape(char: string): string {
  return `${BACKSLASH}u${char.charCodeAt(0).toString(16).padStart(4, '0')}`
}

/**
 * Serialises a value for embedding inside a `<script type="application/ld+json">`
 * element via `dangerouslySetInnerHTML`.
 *
 * `JSON.stringify` alone is not safe there: a string value containing
 * `</script>` closes the tag early, and whatever text follows in the document
 * is then parsed as markup: an attacker-controlled title or FAQ answer can
 * smuggle in a second `<script>` that the browser executes. Escaping every
 * `<`, `>` and `&` to its JSON `\uXXXX` form defeats that, because none of
 * those three characters can then appear literally in the HTML the script
 * element's text node holds. `U+2028` and `U+2029` are escaped for the same
 * reason safe-JSON-for-script libraries always do: they are valid JSON string
 * characters but invalid raw line terminators inside a `<script>` body in
 * some parsers.
 *
 * Every substitution stays inside a JSON string's `\uXXXX` escape, so
 * `JSON.parse(escapeForScript(value))` always returns a value deep-equal to
 * `value`.
 */
export function escapeForScript(value: unknown): string {
  return JSON.stringify(value).replace(UNSAFE_PATTERN, toUnicodeEscape)
}
